import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Package, DollarSign, Info } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACK_SIZES = { 1: 100, 2: 150, 3: 100, 5: 100, 10: 60, 20: 30, 30: 30, 50: 30 };

const formatCurrency = (value, asElement = false) => {
  const num = value || 0;
  const formatted = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(Math.abs(num));
  if (num < 0) {
    return asElement ? <span className="text-red-500">({formatted})</span> : `(${formatted})`;
  }
  return asElement ? <span>{formatted}</span> : formatted;
};

export default function StockView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/stock/current`)
      .then(res => setData(res.data))
      .catch(() => toast.error("Failed to load stock data"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8" data-testid="stock-view-page">
      {/* Header */}
      <div>
        <h1 className="font-mono text-3xl font-bold tracking-tight">Stock Overview</h1>
        <p className="text-muted-foreground mt-1">
          Current inventory from {data?.latest_date ? `latest entry (${data.latest_date})` : 'no entries yet'}
        </p>
      </div>

      {/* Grand Total Card */}
      <Card className="stat-card neon-glow" data-testid="grand-total-card">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="data-label">Total Stock Value</p>
              <p className="data-value text-primary text-4xl">{formatCurrency(data?.grand_total)}</p>
            </div>
            <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
              <DollarSign className="w-8 h-8 text-primary" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 1: Stock Value (with dollar amounts) */}
      <Card className="border-border/50" data-testid="stock-value-section">
        <CardHeader className="py-3">
          <CardTitle className="font-mono text-base flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-primary" />
            Stock Value by Denomination
          </CardTitle>
        </CardHeader>
        <CardContent className="p-2">
          <table className="w-full">
            <thead>
              <tr className="bg-muted/30 text-xs font-mono">
                <th className="p-2 text-left">Lottery</th>
                <th className="p-2 text-center">Sealed Packs</th>
                <th className="p-2 text-center">Pack Value</th>
                <th className="p-2 text-center">Running Tickets</th>
                <th className="p-2 text-center">Ticket Value</th>
                <th className="p-2 text-right">Total Value</th>
              </tr>
            </thead>
            <tbody>
              {data?.stock_value?.map(item => {
                const packValue = item.packs * PACK_SIZES[item.denomination] * item.denomination;
                const ticketValue = item.running_tickets * item.denomination;
                return (
                  <tr key={item.denomination} className="border-t border-border/30 text-sm font-mono" data-testid={`stock-row-${item.denomination}`}>
                    <td className="p-2 text-primary font-bold">${item.denomination}</td>
                    <td className="p-2 text-center">{item.packs}</td>
                    <td className="p-2 text-center text-muted-foreground">{formatCurrency(packValue)}</td>
                    <td className="p-2 text-center">{item.running_tickets}</td>
                    <td className="p-2 text-center text-muted-foreground">{formatCurrency(ticketValue)}</td>
                    <td className="p-2 text-right text-primary font-bold">{formatCurrency(item.total_value)}</td>
                  </tr>
                );
              })}
              {(() => {
                const totals = (data?.stock_value || []).reduce((acc, item) => {
                  const packValue = item.packs * PACK_SIZES[item.denomination] * item.denomination;
                  const ticketValue = item.running_tickets * item.denomination;
                  acc.packs += item.packs || 0;
                  acc.packValue += packValue;
                  acc.runningTickets += item.running_tickets || 0;
                  acc.ticketValue += ticketValue;
                  return acc;
                }, { packs: 0, packValue: 0, runningTickets: 0, ticketValue: 0 });
                return (
                  <tr className="border-t-2 border-primary/50 text-sm font-mono font-bold bg-secondary/30" data-testid="stock-value-totals">
                    <td className="p-2 text-primary uppercase tracking-wide">Total</td>
                    <td className="p-2 text-center text-primary">{totals.packs}</td>
                    <td className="p-2 text-center text-primary">{formatCurrency(totals.packValue)}</td>
                    <td className="p-2 text-center text-primary">{totals.runningTickets}</td>
                    <td className="p-2 text-center text-primary">{formatCurrency(totals.ticketValue)}</td>
                    <td className="p-2 text-right text-primary text-lg">{formatCurrency(data?.grand_total)}</td>
                  </tr>
                );
              })()}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </motion.div>
  );
}
